// Общее поведение всплывающих окон ученика: «Ещё» (src/ui/AppNav.tsx) и
// «Оформление» (src/ui/ThemeSwitch.tsx). Окна могут лежать одно на другом —
// «Оформление» открывается из «Ещё», — и обе ошибки, которые здесь
// закрыты, жили именно в этом сочетании (spec-guard, 29 октября 2026):
//
// 1. Фокус ставится ОДИН раз, при открытии. Эффект с `onClose` в
//    зависимостях перезапускался на каждую перерисовку App — она бывает от
//    любого ответа подписки Convex — и уводил фокус из верхнего окна на
//    крестик нижнего.
// 2. Escape закрывает только ВЕРХНЕЕ окно. У каждого был свой слушатель на
//    window, и одно нажатие закрывало оба.

import { useEffect, useRef, type RefObject } from "react";

const stack: { close: () => void }[] = [];

function onKey(e: KeyboardEvent): void {
  if (e.key !== "Escape" || stack.length === 0) return;
  e.preventDefault();
  stack[stack.length - 1]!.close();
}

export function useDialog(focusFirst: RefObject<HTMLElement | null>, onClose: () => void): void {
  // Свежий обработчик без перезапуска эффекта.
  const latest = useRef(onClose);
  latest.current = onClose;

  useEffect(() => {
    focusFirst.current?.focus();
    const entry = { close: () => latest.current() };
    stack.push(entry);
    if (stack.length === 1) window.addEventListener("keydown", onKey);
    return () => {
      const i = stack.indexOf(entry);
      if (i >= 0) stack.splice(i, 1);
      if (stack.length === 0) window.removeEventListener("keydown", onKey);
    };
    // Только при открытии и закрытии окна — см. пункт 1 вверху файла.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
